import { _ } from "../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import avatarChooserWidget from "../widgets/settings/avatarChooserWidget.js";
import basicInfoWidget from "../widgets/settings/basicInfoWidget.js";
import InternationalizationService from "../services/InternationalizationService.js";
import LanguageChooserWidget from "../widgets/settings/LanguageChooserWidget.js";
import myClientsWidget from "../widgets/settings/myClientsWidget.js";
import TabWidget from "../widgets/TabWidget/TabWidget.js";

/**
 * Tabs for user settings documents both in accounting and agency2.
 *
 * @param {user} spec.currentUser - User currently logged in
 * @param {user} [spec.editedUser] - User to which settings apply
 * @param {boolean} [spec.showBasicInfoTab=false] - `true` if basic info about `this._editedUser` must be displayed. `false` by default
 * @param {boolean} [spec.showCompaniesTab=false] - `true` if a Companies tab must be displayed. `false` by default
 * @param {boolean} [spec.showAvatarChooserTab=false] - True if a Avatar tab must be displayed. False by default
 * @param {function} spec.attachCompanyCallback - Function called when `currentUser` adds a company to `editedUser`
 * @param {function} spec.clientCardFactory - Factory taking a company and returning a cardWidget
 * @param {function} spec.deleteUserCallback - Function called when `currentUser` clicks the delete button
 * @param {function} spec.promoteUserCallback - Function called when `currentUser` clicks the promote button
 * @param {function} spec.demoteUserCallback - Function called when `currentUser` clicks the demote button
 * @param {function} spec.updatePhoneCallback - Function called when `currentUser` clicks the update phone button
 * @param {function} spec.resendInvitationCallback - Function called when `currentUser` clicks the resend invitation button
 * @param {function} spec.updateLanguageCallback - Function called when `currentUser` clicks the change language button
 * @param {function} spec.avatarChosenCallback - Function called when `currentUser` changes his/her avatar
 * @param {function} spec.avatarDeletedCallback - Function called when `currentUser` deletes his/her avatar
 * @param {company[]} [spec.allCompanies] - List of every company the current user can see and which is or could be attached to the edited user. Required only if `currentUser` wants to attach companies to `editedUser`
 * @param {boolean} spec.showAAAUserEditionButtons - Allow the rendering of Edit info and Security
 * settings. Defaults to true.
 */
export default class UserSettingsTabDocumentSection extends Widget {
	constructor({
		currentUser,
		editedUser,
		attachCompanyCallback,
		clientCardFactory,
		deleteUserCallback,
		promoteUserCallback,
		demoteUserCallback,
		updatePhoneCallback,
		resendInvitationCallback,
		updateLanguageCallback,
		avatarChosenCallback,
		avatarDeletedCallback,
		allCompanies,
		showBasicInfoTab,
		showCompaniesTab,
		showAvatarChooserTab,
		showAAAUserEditionButtons = true,
		showLanguageChooserTab = true,
	}) {
		super(...arguments);
		this._currentUser = currentUser;
		this._editedUser = editedUser;
		this._attachCompanyCallback = attachCompanyCallback;
		this._clientCardFactory = clientCardFactory;
		this._deleteUserCallback = deleteUserCallback;
		this._promoteUserCallback = promoteUserCallback;
		this._demoteUserCallback = demoteUserCallback;
		this._updatePhoneCallback = updatePhoneCallback;
		this._resendInvitationCallback = resendInvitationCallback;
		this._updateLanguageCallback = updateLanguageCallback;
		this._avatarChosenCallback = avatarChosenCallback;
		this._avatarDeletedCallback = avatarDeletedCallback;
		this._allCompanies = allCompanies;
		this._showBasicInfoTab = !!showBasicInfoTab;
		this._showCompaniesTab = !!showCompaniesTab;
		this._showLanguageChooserTab = !!showLanguageChooserTab;
		this._showAvatarChooserTab = !!showAvatarChooserTab;
		this._showAAAUserEditionButtons = showAAAUserEditionButtons;

		this._languageChooser = new LanguageChooserWidget({
			currentLanguage: this._editedUser && this._editedUser.getLanguage(),
			service: InternationalizationService.instance(),
		});

		this._languageChooser.onUpdateLanguage.register(
			this._updateLanguageCallback,
		);

		this._clientsList = myClientsWidget({
			currentUser: this._currentUser,
			editedUser: this._editedUser,
			attachCompanyCallback: this._attachCompanyCallback,
			clientCardFactory: this._clientCardFactory,
			allCompanies: this._allCompanies,
		});

		this._basicInfoWidget = basicInfoWidget({
			currentUser: this._currentUser,
			editedUser: this._editedUser,
			deleteUserCallback: this._deleteUserCallback,
			promoteUserCallback: this._promoteUserCallback,
			demoteUserCallback: this._demoteUserCallback,
			updatePhoneCallback: this._updatePhone.bind(this),
			resendInvitationCallback: this._resendInvitationCallback,
			showAAAUserEditionButtons: this._showAAAUserEditionButtons,
		});

		this._avatarChooserWidget = avatarChooserWidget({
			currentImage: this._editedUser
				? this._editedUser.getPictureUrl()
				: undefined,
			avatarChosenCallback: (data) =>
				this._avatarChosenCallback(this._editedUser, data),
			avatarDeletedCallback: () =>
				this._avatarDeletedCallback(this._editedUser),
			showDeleteButton: () =>
				this._editedUser &&
				!this._editedUser.isUsingDefaultPictureUrl(),
		});

		this._tabsWidget = new TabWidget({
			tabs: [
				{
					name: _("Basic Info"),
					id: "tab-user-info",
					content: this._basicInfoWidget,
					enabled: this._showBasicInfoTab,
				},
				{
					name: _("Avatar"),
					id: "tab-user-image",
					content: this._avatarChooserWidget,
					enabled: this._showAvatarChooserTab,
				},
				{
					name: _("Language"),
					id: "tab-language",
					content: this._languageChooser,
					enabled: this._showLanguageChooserTab,
				},
				{
					name: _("Companies"),
					id: "tab-company-info",
					content: this._clientsList,
					enabled: this._showCompaniesTab,
				},
			],
			tabsOnlyOnMobile: true,
			hideLabelsOnMobile: true,
		});
	}

	setEditedUser(editedUser) {
		this._editedUser = editedUser;

		this._languageChooser.setLanguage(this._editedUser.getLanguage());

		this._clientsList.setEditedUser(this._editedUser);
		this._basicInfoWidget.setEditedUser(this._editedUser);

		this._avatarChooserWidget.setCurrentImage(
			this._editedUser.getPictureUrl(),
		);

		this.update();
	}

	renderContentOn(html) {
		html.render(this._tabsWidget);
	}

	_updatePhone(info) {
		if (!this._editedUser) {
			return;
		}

		this._editedUser.setPhone(info.phone);

		this._updatePhoneCallback(info);
	}
}
